import { useCallback, useState } from 'react'
import { LegalDocumentDialog } from './LegalDocumentDialog'

/**
 * Уведомление о согласии под кнопкой входа или регистрации.
 *
 * Стоит именно под кнопкой и без флажка: согласие даётся действием,
 * и текст это прямо называет. Отдельный чекбокс добавил бы шаг,
 * ничего не добавив юридически, — момент согласия всё равно
 * фиксируется на сервере при регистрации.
 *
 * Названия документов открывают текст внутри приложения, а не ведут
 * на внешний сайт: документ хранится в настройках владельца, поэтому
 * прочитать его можно до входа и без риска попасть на переехавшую
 * страницу. Сам текст грузит fetchDocument — пакет ui не ходит
 * в сеть, иначе экран входа нельзя было бы отрисовать в тесте
 * без мока HTTP-клиента.
 *
 * fetchDocument(key) → Promise<{ title, text }>, key —
 * 'user_agreement' | 'privacy_policy'.
 */
export function LegalConsent({ legal, fetchDocument, className = '' }) {
  const [doc, setDoc] = useState(null) // { key, title, text, loading, error }

  const openDoc = useCallback(
    async (key, fallbackTitle) => {
      if (!fetchDocument) return
      setDoc({ key, title: fallbackTitle, text: '', loading: true, error: '' })
      try {
        const data = await fetchDocument(key)
        setDoc({
          key,
          title: data?.title || fallbackTitle,
          text: data?.text || '',
          loading: false,
          error: '',
        })
      } catch {
        setDoc({
          key,
          title: fallbackTitle,
          text: '',
          loading: false,
          error: 'Не удалось загрузить документ',
        })
      }
    },
    [fetchDocument]
  )

  if (!legal || legal.enabled === false) return null

  const docButton = (key, text) => (
    <button
      type="button"
      className="legal-consent__link"
      onClick={() => openDoc(key, text)}
    >
      {text}
    </button>
  )

  return (
    <>
      <p className={`legal-consent ${className}`.trim()}>
        {legal.consent_prefix}{' '}
        {docButton('user_agreement', legal.user_agreement_title)}
        {' и '}
        {docButton('privacy_policy', legal.privacy_policy_title)}
      </p>

      <LegalDocumentDialog
        open={doc !== null}
        title={doc?.title}
        text={doc?.text}
        loading={doc?.loading}
        error={doc?.error}
        onClose={() => setDoc(null)}
        onRetry={() => doc && openDoc(doc.key, doc.title)}
      />
    </>
  )
}
